import React, { useEffect, useMemo, useState } from 'react'
import { PencilIcon, PlusIcon, TrashIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, SortableTableHeader, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { estoqueService } from '../../services/estoque'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import type { EstoqueCategoria, EstoqueItem, EstoqueItemFoto, EstoqueItemType } from '../../types/estoque'
import EstoqueModal, { EstoqueModalActions } from './EstoqueModal'
import EstoquePagination from './EstoquePagination'
import {
  ICON_BTN_CLASS,
  ICON_BTN_DANGER_CLASS,
  MODULE_NAME,
  PAGE_SIZE,
  PERM_LOADING,
  SELECT_CLASS,
  TEXTAREA_CLASS,
  formatQuantity,
  itemTypeLabel,
  paginateRows,
  sortRows,
  statusBadgeClass,
} from './estoqueUi'

type Props = {
  itens: EstoqueItem[]
  categorias: EstoqueCategoria[]
  loading: boolean
  onChanged: () => void
  canInsert: boolean
  canUpdate: boolean
  canDelete: boolean
}
type FormState = {
  name: string
  item_type: EstoqueItemType
  category_id: string
  unit: string
  min_stock: number
  description: string
  is_active: boolean
  available_for_request: boolean
}

const emptyForm: FormState = {
  name: '',
  item_type: 'material',
  category_id: '',
  unit: 'un',
  min_stock: 0,
  description: '',
  is_active: true,
  available_for_request: false,
}

const EstoqueItensTab: React.FC<Props> = ({
  itens,
  categorias,
  loading,
  onChanged,
  canInsert,
  canUpdate,
  canDelete,
}) => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const [sortKey, setSortKey] = useState('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<EstoqueItem | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fotos, setFotos] = useState<(EstoqueItemFoto & { signed_url?: string | null })[]>([])
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [search, setSearch] = useState('')
  const [itemType, setItemType] = useState<EstoqueItemType | ''>('')
  const [categoriaId, setCategoriaId] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const activeCategorias = categorias.filter((c) => c.is_active)
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return itens.filter((row) => {
      if (itemType && row.item_type !== itemType) return false
      if (categoriaId && row.category_id !== categoriaId) return false
      if (statusFilter === 'active' && !row.is_active) return false
      if (statusFilter === 'inactive' && row.is_active) return false
      if (term) {
        const name = row.name.toLowerCase()
        const code = String(row.code ?? '').toLowerCase()
        if (!name.includes(term) && !code.includes(term)) return false
      }
      return true
    })
  }, [itens, search, itemType, categoriaId, statusFilter])
  const sorted = useMemo(
    () =>
      sortRows(filtered, sortKey, sortOrder, (row, key) => {
        if (key === 'name') return row.name
        if (key === 'type') return row.item_type
        if (key === 'categoria') return row.category?.name
        if (key === 'minimo') return Number(row.min_stock)
        if (key === 'active') return row.is_active ? 1 : 0
        return row.code
      }),
    [filtered, sortKey, sortOrder]
  )
  const pageRows = paginateRows(sorted, page)
  const hasActiveFilters = Boolean(search.trim() || itemType || categoriaId || statusFilter !== 'all')
  const clearFilters = () => {
    setSearch('')
    setItemType('')
    setCategoriaId('')
    setStatusFilter('all')
    setPage(1)
  }
  const toggleSort = (key: string) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortOrder('asc')
    }
    setPage(1)
  }
  useEffect(() => {
    if (!modalOpen || !editing) {
      setFotos([])
      return
    }
    let cancelled = false
    ;(async () => {
      const list = await estoqueService.listItemFotos(editing.id)
      const withUrls = await Promise.all(
        list.map(async (foto) => ({
          ...foto,
          signed_url: await estoqueService.getItemFotoUrl(foto.file_path),
        }))
      )
      if (!cancelled) setFotos(withUrls)
    })()
    return () => {
      cancelled = true
    }
  }, [modalOpen, editing])
  const openCreate = () => {
    setEditing(null)
    setForm({
      ...emptyForm,
      category_id: activeCategorias[0]?.id || '',
    })
    setPendingFiles([])
    setFotos([])
    setError(null)
    setModalOpen(true)
  }
  const openEdit = (row: EstoqueItem) => {
    setEditing(row)
    setForm({
      name: row.name,
      item_type: row.item_type,
      category_id: row.category_id,
      unit: row.unit || 'un',
      min_stock: Number(row.min_stock || 0),
      description: row.description || '',
      is_active: row.is_active,
      available_for_request: Boolean(row.available_for_request),
    })
    setPendingFiles([])
    setError(null)
    setModalOpen(true)
  }
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('Nome é obrigatório.')
      return
    }
    if (!form.category_id) {
      setError('Categoria é obrigatória.')
      return
    }
    if (form.min_stock < 0) {
      setError('Estoque mínimo deve ser maior ou igual a zero.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      let itemId = editing?.id
      let itemName = form.name.trim()
      if (editing) {
        const updated = await estoqueService.updateItem(editing.id, form)
        void logUpdate(
          'marketing_stock_item',
          updated.id,
          `Atualizou item de estoque: ${updated.name}`,
          {
            name: editing.name,
            item_type: editing.item_type,
            category_id: editing.category_id,
            unit: editing.unit,
            min_stock: editing.min_stock,
            description: editing.description,
            is_active: editing.is_active,
            available_for_request: editing.available_for_request,
          },
          {
            name: updated.name,
            item_type: updated.item_type,
            category_id: updated.category_id,
            unit: updated.unit,
            min_stock: updated.min_stock,
            description: updated.description,
            is_active: updated.is_active,
            available_for_request: updated.available_for_request,
          }
        )
        itemId = updated.id
        itemName = updated.name
      } else {
        const created = await estoqueService.createItem(form)
        void logCreate(
          'marketing_stock_item',
          created.id,
          `Criou item de estoque: ${created.name}`,
          {
            name: created.name,
            code: created.code,
            item_type: created.item_type,
            category_id: created.category_id,
            unit: created.unit,
            min_stock: created.min_stock,
            is_active: created.is_active,
            available_for_request: created.available_for_request,
          }
        )
        itemId = created.id
        itemName = created.name
      }
      if (itemId && pendingFiles.length > 0) {
        setUploading(true)
        for (const file of pendingFiles) {
          const foto = await estoqueService.uploadItemFoto(file, itemId, user?.id)
          void logCreate(
            'marketing_stock_item_photo',
            foto.id,
            `Anexou foto ao item de estoque: ${itemName}`,
            {
              item_id: itemId,
              file_name: foto.file_name,
              file_size: foto.file_size,
              mime_type: foto.mime_type,
            }
          )
        }
      }
      setModalOpen(false)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar item.')
    } finally {
      setUploading(false)
      setSaving(false)
    }
  }
  const handleDelete = async (row: EstoqueItem) => {
    if (!window.confirm(`Excluir o item "${row.name}"?`)) return
    try {
      const result = await estoqueService.softDeleteItem(row.id, {
        deleted_by: user?.id,
        deleted_reason: 'Exclusão pela tela de estoque',
      })
      void logDelete(
        'marketing_stock_item',
        row.id,
        result === 'inactivated'
          ? `Inativou item de estoque com movimentações: ${row.name}`
          : `Excluiu item de estoque: ${row.name}`,
        {
          name: row.name,
          item_type: row.item_type,
          category_id: row.category_id,
          inactivated: result === 'inactivated',
        }
      )
      onChanged()
      if (result === 'inactivated') {
        window.alert('O item possui movimentações e foi inativado.')
      }
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erro ao excluir item.')
    }
  }
  const handleRemoveFoto = async (foto: EstoqueItemFoto) => {
    if (!window.confirm('Remover esta foto?')) return
    try {
      await estoqueService.softDeleteItemFoto(foto.id, {
        deleted_by: user?.id,
        deleted_reason: 'Remoção de foto do item',
      })
      void logDelete(
        'marketing_stock_item_photo',
        foto.id,
        `Removeu foto do item de estoque: ${editing?.name ?? foto.item_id}`,
        {
          item_id: foto.item_id,
          file_name: foto.file_name,
          file_path: foto.file_path,
        }
      )
      setFotos((prev) => prev.filter((f) => f.id !== foto.id))
      onChanged()
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erro ao remover foto.')
    }
  }
  const modalBusy = saving || uploading

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
            Itens
          </Heading>
          <Text size="sm" color="muted" className="mt-1">
            Cadastre materiais e brindes com categoria, estoque mínimo e fotos.
          </Text>
        </div>
        {canInsert && (
          <PermissionGuard moduleName={MODULE_NAME} permissionType="insert" loadingComponent={PERM_LOADING} fallback={null}>
            <Button variant="solid" onClick={openCreate} disabled={activeCategorias.length === 0} className="flex items-center gap-1">
              <PlusIcon className="h-5 w-5" />
              Novo item
            </Button>
          </PermissionGuard>
        )}
      </div>
      {activeCategorias.length === 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          Cadastre ao menos uma categoria ativa antes de criar itens.
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Buscar</label>
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            placeholder="Buscar por nome"
            className="min-w-[180px]"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo</label>
          <select
            value={itemType}
            onChange={(e) => {
              setItemType(e.target.value as EstoqueItemType | '')
              setPage(1)
            }}
            className={SELECT_CLASS}
          >
            <option value="">Todos</option>
            <option value="material">Material</option>
            <option value="brinde">Brinde</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Categoria</label>
          <select
            value={categoriaId}
            onChange={(e) => {
              setCategoriaId(e.target.value)
              setPage(1)
            }}
            className={SELECT_CLASS}
          >
            <option value="">Todas</option>
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')
              setPage(1)
            }}
            className={SELECT_CLASS}
          >
            <option value="all">Todos</option>
            <option value="active">Ativo</option>
            <option value="inactive">Inativo</option>
          </select>
        </div>
        <div className="flex items-end">
          <Button
            variant="outline"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="flex items-center gap-1 whitespace-nowrap"
          >
            <XCircleIcon className="h-4 w-4" />
            Limpar filtros
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <SortableTableHeader sortKey="name" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Nome
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="type" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Tipo
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="categoria" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Categoria
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Unidade</th>
                  <SortableTableHeader sortKey="minimo" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3 text-right">
                    Mínimo
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="active" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Status
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      Nenhum item encontrado.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/20">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {row.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{itemTypeLabel(row.item_type)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{row.category?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{row.unit}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">{formatQuantity(row.min_stock)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(row.is_active)}`}>
                          {row.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canUpdate && (
                            <PermissionGuard moduleName={MODULE_NAME} permissionType="update" loadingComponent={PERM_LOADING} fallback={null}>
                              <button type="button" onClick={() => openEdit(row)} className={ICON_BTN_CLASS} title="Editar">
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            </PermissionGuard>
                          )}
                          {canDelete && (
                            <PermissionGuard moduleName={MODULE_NAME} permissionType="delete" loadingComponent={PERM_LOADING} fallback={null}>
                              <button type="button" onClick={() => void handleDelete(row)} className={ICON_BTN_DANGER_CLASS} title="Excluir">
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </PermissionGuard>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <EstoquePagination page={page} total={sorted.length} onPageChange={setPage} pageSize={PAGE_SIZE} />
      {modalOpen && (
        <EstoqueModal
          title={editing ? 'Editar item' : 'Novo item'}
          onClose={() => setModalOpen(false)}
          busy={modalBusy}
          onSubmit={handleSave}
          maxWidthClass="max-w-2xl"
          footer={<EstoqueModalActions onCancel={() => setModalOpen(false)} saving={modalBusy} />}
        >
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Nome <span className="text-red-500">*</span>
              </label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Tipo <span className="text-red-500">*</span>
              </label>
              <select
                value={form.item_type}
                onChange={(e) => setForm((f) => ({ ...f, item_type: e.target.value as EstoqueItemType }))}
                className={`w-full ${SELECT_CLASS}`}
              >
                <option value="material">Material</option>
                <option value="brinde">Brinde</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Categoria <span className="text-red-500">*</span>
              </label>
              <select
                value={form.category_id}
                onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}
                className={`w-full ${SELECT_CLASS}`}
                required
              >
                <option value="">Selecione</option>
                {categorias
                  .filter((c) => c.is_active || c.id === form.category_id)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Unidade</label>
              <Input value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Estoque mínimo</label>
              <Input
                type="number"
                min={0}
                step="any"
                value={form.min_stock}
                onChange={(e) => setForm((f) => ({ ...f, min_stock: Number(e.target.value) }))}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Descrição</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
                className={TEXTAREA_CLASS}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Ativo
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={form.available_for_request}
                onChange={(e) =>
                  setForm((f) => ({ ...f, available_for_request: e.target.checked }))
                }
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Disponível para solicitação
            </label>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Fotos</label>
            {editing && fotos.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-3">
                {fotos.map((foto) => (
                  <div key={foto.id} className="relative h-24 w-24 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600">
                    {foto.signed_url ? (
                      <img src={foto.signed_url} alt={foto.file_name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-gray-400">Sem preview</div>
                    )}
                    {(canUpdate || canDelete) && (
                      <button
                        type="button"
                        onClick={() => void handleRemoveFoto(foto)}
                        className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"
                        title="Remover foto"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || [])
                setPendingFiles((prev) => [...prev, ...files])
                e.target.value = ''
              }}
              className="block w-full text-sm text-gray-600 dark:text-gray-300"
            />
            {pendingFiles.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-400">
                {pendingFiles.map((file, idx) => (
                  <li key={`${file.name}-${idx}`} className="flex items-center justify-between gap-2">
                    <span>{file.name}</span>
                    <button
                      type="button"
                      className="text-red-600 dark:text-red-400"
                      onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      Remover
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </EstoqueModal>
      )}
    </div>
  )
}

export default EstoqueItensTab
